import type {PosterPageData} from '@Libraries/poster-library';
import {noop} from '@Utils/general.util';
import styles from '@Components/design-selector/design-selector.module.scss';

const DESIGN_SELECTOR_PAGES_LIMIT = 5;

/**
 * Opens the design selector for the email editor flow. Thin opts builder over the
 * `PMW.designSelector` coordinator — the coordinator owns selection state and all DOM visuals.
 * The email-specific config here is the 5-page warning banner.
 */
export function openDesignSelector(
  onDesignLoaded: (data: PosterPageData[]) => void,
  beforeLoad: VoidFunction = noop,
  isMultiselectAllowed = true,
  addCheckmarkOnSelection = true,
  showPageLimitWarning = true
): void {
  window.PMW.util.require(typeof window.PMW.designSelector === 'undefined', 'designSelector', true, () => {
    beforeLoad();
    window.PMW.designSelector.open({
      title: window.i18next.t('pmwjs_select_a_design'),
      isMultiSelectionAllowed: isMultiselectAllowed,
      addCheckmarkOnSelection,
      selectedNameClass: styles.selectedName,
      pageLimitBanner:
        showPageLimitWarning && isMultiselectAllowed
          ? {
              threshold: DESIGN_SELECTOR_PAGES_LIMIT,
              desktopMessageKey: 'pmwjs_email_editor_page_limit_message_desktop',
              mobileMessageKey: 'pmwjs_email_editor_page_limit_message_mobile',
            }
          : undefined,
      onAddSelected: onDesignLoaded,
    });
  });
}

/**
 * Single-select design-picker for callers that only need a poster ID + preview URL.
 * Used by the legacy email-editor design block insertion when multipage UI isn't enabled.
 */
export function openDesignSelectorLegacy(onDesignLoaded: (id: string, imagePreviewUrl: string | undefined) => void, beforeLoad: VoidFunction = noop): void {
  window.PMW.util.require(typeof window.PMW.designSelector === 'undefined', 'designSelector', true, () => {
    beforeLoad();
    window.PMW.designSelector.open({
      title: window.i18next.t('pmwjs_select_a_design'),
      addSelectedCtaText: window.i18next.t('pmwjs_select'),
      isMultiSelectionAllowed: false,
      addCheckmarkOnSelection: false,
      onAddSelected: (selection: PosterPageData[]): void => {
        if (selection.length > 0) {
          onDesignLoaded(selection[0].posterId, selection[0].previewUrl);
        }
      },
    });
  });
}
